// Upload pipeline hook: extract (browser) -> chunk (browser) -> embed/store
// (batches of 20 chunks to /api/manuals) -> finalize.
//
// Phases: idle | extracting | uploading | finalizing | done | cancelled | error
// Cancelling mid-upload deletes the half-created document server-side.
import { useCallback, useRef, useState } from 'react';
import { extractPdfText, ExtractionCancelledError } from './pdfExtract';
import { chunkText, isProbablyScanned, pagesToText } from '../lib/chunking';
import { callManualsApi } from './manualsApi';

export const MAX_PDF_BYTES = 50 * 1024 * 1024; // 50MB
const CHUNKS_PER_BATCH = 20;

const INITIAL = {
  phase: 'idle',
  extractPct: 0,
  uploadPct: 0,
  error: null,
};

export function useManualUpload() {
  const [state, setState] = useState(INITIAL);
  const cancelRef = useRef(false);

  const reset = useCallback(() => {
    cancelRef.current = false;
    setState(INITIAL);
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const start = useCallback(async (file, equipmentPath) => {
    cancelRef.current = false;

    // --- Local validation, before any work -------------------------------
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf')) {
      setState({ ...INITIAL, phase: 'error', error: 'Only PDF files are supported.' });
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setState({ ...INITIAL, phase: 'error', error: 'This file is larger than 50MB.' });
      return;
    }
    if (!equipmentPath) {
      setState({ ...INITIAL, phase: 'error', error: 'Please select the equipment first.' });
      return;
    }

    let documentId = null;
    try {
      // --- 1. Extract text in the browser --------------------------------
      setState({ ...INITIAL, phase: 'extracting' });
      const pages = await extractPdfText(file, {
        shouldCancel: () => cancelRef.current,
        onProgress: (done, total) =>
          setState(s => ({ ...s, extractPct: Math.round((done / total) * 100) })),
      });

      if (isProbablyScanned(pages)) {
        setState({
          ...INITIAL,
          phase: 'error',
          error:
            'This looks like a scanned PDF (no text layer). Scanned PDFs are not supported yet.',
        });
        return;
      }

      const chunks = chunkText(pagesToText(pages));
      if (chunks.length === 0) {
        setState({ ...INITIAL, phase: 'error', error: 'No readable text found in this PDF.' });
        return;
      }
      if (cancelRef.current) {
        setState({ ...INITIAL, phase: 'cancelled' });
        return;
      }

      // --- 2. Create the document (checks the plan limit server-side) ----
      setState(s => ({ ...s, phase: 'uploading', extractPct: 100, uploadPct: 0 }));
      const created = await callManualsApi('create_document', {
        filename: file.name,
        equipment_path: equipmentPath,
      });
      documentId = created.document_id;

      // --- 3. Upload chunk batches (embedding happens server-side) -------
      for (let i = 0; i < chunks.length; i += CHUNKS_PER_BATCH) {
        if (cancelRef.current) {
          await callManualsApi('delete_document', { document_id: documentId }).catch(() => {});
          setState({ ...INITIAL, phase: 'cancelled' });
          return;
        }
        await callManualsApi('add_chunks', {
          document_id: documentId,
          chunks: chunks.slice(i, i + CHUNKS_PER_BATCH),
        });
        setState(s => ({
          ...s,
          uploadPct: Math.round(Math.min(100, ((i + CHUNKS_PER_BATCH) / chunks.length) * 100)),
        }));
      }

      // --- 4. Finalize ----------------------------------------------------
      setState(s => ({ ...s, phase: 'finalizing', uploadPct: 100 }));
      await callManualsApi('finalize', { document_id: documentId });
      setState(s => ({ ...s, phase: 'done' }));
    } catch (e) {
      if (e instanceof ExtractionCancelledError) {
        setState({ ...INITIAL, phase: 'cancelled' });
        return;
      }
      // If the document was created but the upload died, clean it up so it
      // doesn't hold a plan slot as a stuck "processing" row.
      if (documentId) {
        await callManualsApi('delete_document', { document_id: documentId }).catch(() => {});
      }
      setState({
        ...INITIAL,
        phase: 'error',
        error: e.message || 'Upload failed. Please try again.',
      });
    }
  }, []);

  return { ...state, start, cancel, reset };
}
